import { useEffect } from 'react';

/**
 * Applies the site-wide scroll-reveal animation to every <section> under
 * <main>, gated on client-side hydration. Sections marked `no-scroll-reveal`
 * (the shared hero, BaseHeroSection.tsx) are skipped entirely — they must
 * paint immediately since they contain the LCP element, and holding them at
 * opacity:0 until hydration completes was measured to cost ~92% of LCP time.
 */
export function useScrollAnimation(pathname: string) {
  useEffect(() => {
    if (typeof window === 'undefined' || !window.IntersectionObserver) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      },
    );

    const mainEl = document.querySelector('main');
    if (!mainEl) return;

    // Track timeouts to clean them up properly on unmount
    const timeouts = new Set<number>();

    const processSection = (section: HTMLElement, retries = 0) => {
      // Check if React has hydrated this element by looking for internal React properties
      const isHydrated = Object.keys(section).some((key) =>
        key.startsWith('__react'),
      );

      if (!isHydrated && retries < 20) {
        // Defer processing until the component has hydrated, up to 1 second
        const id = window.setTimeout(() => {
          processSection(section, retries + 1);
          timeouts.delete(id);
        }, 50);
        timeouts.add(id);
        return;
      }

      if (
        section.tagName.toLowerCase() === 'section' &&
        !section.classList.contains('custom-animated-section') &&
        !section.classList.contains('animate-section') &&
        !section.classList.contains('no-scroll-reveal')
      ) {
        // Previously read section.getBoundingClientRect() here to decide
        // whether a section was already in view. That read was confirmed
        // (via a Chrome Performance trace with an attached JS stack) to
        // force a synchronous layout recalculation, since it runs
        // interleaved with React's own concurrent hydration DOM writes.
        // IntersectionObserver reports initial visibility asynchronously
        // instead, so observing every section (rather than only the
        // not-yet-visible ones) gets the same eventual reveal behavior
        // without forcing layout — already-visible sections just get
        // `is-visible` a frame or two later instead of synchronously, which
        // is imperceptible for non-hero content.
        window.requestAnimationFrame(() => {
          section.classList.add('animate-section');
          observer.observe(section);
        });
      }
    };

    // 1. Process initial sections in the main element
    const initialSections = mainEl.querySelectorAll('section');
    initialSections.forEach((sec) => processSection(sec as HTMLElement));

    // 2. Watch for dynamic sections added after layout mount (e.g. via Suspense, dynamic routing)
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            if (node.tagName.toLowerCase() === 'section') {
              processSection(node);
            } else {
              const nestedSections = node.querySelectorAll('section');
              nestedSections.forEach((sec) =>
                processSection(sec as HTMLElement),
              );
            }
          }
        });
      });
    });

    mutationObserver.observe(mainEl, {
      childList: true,
      subtree: true,
    });

    return () => {
      timeouts.forEach((id) => window.clearTimeout(id));
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [pathname]);
}
